import { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'hooks';
import { getDeviceOrderings } from 'store/groups/reducer';
import { fetchDeviceOrderings } from 'store/groups/service';
import { ModalV2 } from 'components/UI';

import { AccessoryCard } from './AccessoryCard';

interface AccessoriesModalProps {
  device: IDeviceListItem;
  patient?: IPatient | null;
  onClose: () => void;
}

const AccessoriesModal = ({ device, patient, onClose }: AccessoriesModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const option = device.options;
  const catalog = useAppSelector(getDeviceOrderings) ?? [];

  useEffect(() => {
    if (device.gid) {
      dispatch(fetchDeviceOrderings(device.gid));
    }
  }, [device.gid, dispatch]);

  const isAssignedOption = (catalogItem: IDeviceOrderingOption) =>
    Boolean(option) &&
    (catalogItem.deviceOrderingId === option?.deviceOrderingId || catalogItem.sku === option?.sku);

  const catalogEntry = catalog.find(
    (item) =>
      item.deviceOrderingId === device.deviceOrdering.deviceOrderingId ||
      item.options.some(isAssignedOption)
  );

  const defaultOption = catalogEntry?.options.find((catalogItem) => catalogItem.isDefaultAccessory);
  const assignedOption = catalogEntry?.options.find(isAssignedOption);
  const hasNonDefaultAssignedOption = assignedOption && !assignedOption.isDefaultAccessory;

  const patientSource = device.patient ?? patient;
  const patientName = patientSource
    ? `${patientSource.lastName}, ${patientSource.firstName}`
    : undefined;

  return (
    <ModalV2 show onHide={onClose}>
      <ModalV2.Header closeButton>
        <div>
          <ModalV2.Title>{intl.formatMessage({ id: 'accessoriesKey' })}</ModalV2.Title>
          <div className="text-secondary">
            {intl.formatMessage({ id: device.deviceOrdering.displayName })}
          </div>
          {patientName && <div className="text-secondary small">{patientName}</div>}
        </div>
      </ModalV2.Header>
      <ModalV2.Body>
        {defaultOption && (
          <div className="mb-3">
            <p className="text-uppercase mb-2 small fw-semibold text-secondary device-group-label">
              {intl.formatMessage({ id: 'shippedWithDeviceKey' })}
            </p>
            <AccessoryCard option={defaultOption} />
          </div>
        )}
        {hasNonDefaultAssignedOption && assignedOption && (
          <div>
            <p className="text-uppercase mb-2 small fw-semibold text-secondary device-group-label">
              {intl.formatMessage({ id: 'additionalCuffsKey' })}
            </p>
            <AccessoryCard option={assignedOption} />
          </div>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Button variant="primary" onClick={onClose}>
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default AccessoriesModal;
